// Geometria de lane do canvas por estágios (hit-test de drag, dimensões visuais). Separado de
// flow-helpers.ts (grafo/camadas, sem noção de pixel) por responsabilidade: aqui é geometria de UI,
// consumido por useSandboxFlow (montagem de nodes) e pelo handler de onNodeDragStop.
import type { Node } from "reactflow";
import type { SandboxLaneNodeData, SandboxMachine, SandboxStage } from "@/types/sim-sandbox.types";
import { machinesInStage } from "./flow-helpers";

export const COL_W = 220;
export const ROW_H = 96;
export const X0 = 40;
/** Altura do header da lane REAL (nome + contagem + ações). Única fonte — usada tanto na altura
 * do retângulo da lane quanto no offset Y da 1ª linha de máquina. Calibrada pro conteúdo real do
 * header em SandboxLaneNode.tsx (padding do NodeShell + do card interno + Input h-7 + linha de
 * contagem/botões + botão "+ Máquina" size=sm) — se o header ganhar mais conteúdo, recalibrar
 * junto, senão a 1ª linha de máquina volta a cobrir o header (nome de estágio muito longo que
 * quebra em 2 linhas ainda pode estourar; não coberto por este valor fixo). */
export const LANE_HEADER_H = 132;
/** Altura da lane-fantasma "+ Estágio" — independente de `LANE_HEADER_H`: seu conteúdo é só um
 * botão, não o header inteiro. */
export const GHOST_LANE_H = 64;
const LANE_PAD = 8;

export interface LaneRect {
  stageKey: string;
  xMin: number;
  xMax: number;
  height: number;
  /** Máquinas PRESENTES no estágio (mesma contagem exibida no header — "X máquinas"). */
  presentCount: number;
}

/** Retângulos de cada estágio (ativo ou não) em coordenadas do canvas, na ordem de `order`. Altura
 * conta TODAS as máquinas do estágio (inclusive desligadas) — casa com `useSandboxFlow`, que
 * também não filtra `present` ao atribuir a linha (row) de cada nó; senão uma máquina desligada
 * "rouba" uma linha que a altura não reservou, e a máquina seguinte vaza pra fora da lane. */
export function laneRects(stages: SandboxStage[], machines: SandboxMachine[]): LaneRect[] {
  const ordered = [...stages].sort((a, b) => a.order - b.order);
  return ordered.map((s, i) => {
    const rowCount = machines.filter((m) => m.stageKey === s.key).length;
    const presentCount = machinesInStage(s.key, machines).length;
    const x = X0 + (i + 1) * COL_W;
    return {
      stageKey: s.key,
      xMin: x - LANE_PAD,
      xMax: x + COL_W - LANE_PAD,
      height: LANE_HEADER_H + Math.max(rowCount, 1) * ROW_H + LANE_PAD,
      presentCount,
    };
  });
}

/** Estágio cujo retângulo contém `x`; clampa antes da 1ª lane; null a partir do fim da última
 * lane real (sinal para criar um estágio novo) — sem margem de tolerância: a lane-fantasma
 * "+ Estágio" já ocupa visualmente todo esse espaço (começa logo em `last.xMax + LANE_PAD`), então
 * qualquer tolerância aqui vazaria pra dentro da área que o usuário vê como "criar novo". */
export function stageKeyForX(x: number, rects: LaneRect[]): string | null {
  if (rects.length === 0) return null;
  const first = rects[0]!;
  if (x < first.xMin) return first.stageKey;
  for (const r of rects) {
    if (x >= r.xMin && x <= r.xMax) return r.stageKey;
  }
  return null;
}

export type DropResolution =
  | { action: "move"; stageKey: string }
  | { action: "create" }
  | { action: "none" };

/** Decide o efeito de soltar uma máquina em `x`: mover pra lane sob o cursor, criar uma nova
 * (além da última) ou nada (soltou na própria lane). Puro — testável sem montar React Flow. */
export function resolveDropTarget(
  currentStageKey: string,
  x: number,
  rects: LaneRect[]
): DropResolution {
  const target = stageKeyForX(x, rects);
  if (target === null) return { action: "create" };
  if (target === currentStageKey) return { action: "none" };
  return { action: "move", stageKey: target };
}

/** Shell comum a toda STAGE_LANE (real ou fantasma) — width/height explícitos (não só `style`)
 * evitam o React Flow precisar medir via ResizeObserver antes de exibir o node — sem isso ele
 * nasce `visibility:hidden` até medir, o que nunca acontece em jsdom e quebra testes RTL. */
function laneNode(
  id: string,
  x: number,
  width: number,
  height: number,
  data: SandboxLaneNodeData
): Node<SandboxLaneNodeData> {
  return {
    id,
    type: "STAGE_LANE",
    position: { x, y: 0 },
    draggable: false,
    selectable: false,
    zIndex: -1,
    width,
    height,
    style: { width, height },
    data,
  };
}

/** Nodes de fundo (STAGE_LANE) por estágio + uma lane-fantasma "+ Estágio" ao final (só quando
 * editável). `onAddMachine` é injetado aqui (não é puro no sentido estrito por causa da closure,
 * mas não afeta a geometria testada isoladamente por `laneRects`/`stageKeyForX`). */
export function buildLaneNodes(
  stages: SandboxStage[],
  machines: SandboxMachine[],
  editable: boolean,
  onAddMachine: (stageKey: string) => void,
  // Estimativa exibida no próprio desenho: vazão por estágio + qual limita a linha.
  estimate: {
    perStage?: Array<{ stageKey: string; throughputUnH: number }>;
    bottleneckStageKey?: string | null;
  } = {}
): Node<SandboxLaneNodeData>[] {
  const throughputByStage = new Map(
    (estimate.perStage ?? []).map((p) => [p.stageKey, p.throughputUnH])
  );
  const ordered = [...stages].sort((a, b) => a.order - b.order);
  const rects = laneRects(stages, machines);
  const totalStages = ordered.length;
  const lanes = ordered.map((s, i) => {
    const rect = rects[i]!;
    return laneNode(`lane-${s.key}`, rect.xMin, rect.xMax - rect.xMin, rect.height, {
      stageKey: s.key,
      name: s.name,
      count: rect.presentCount,
      index: i,
      totalStages,
      editable,
      isGhost: false,
      onAddMachine,
      throughputUnH: throughputByStage.get(s.key) ?? null,
      isBottleneck: estimate.bottleneckStageKey === s.key,
    });
  });
  if (!editable) return lanes;
  const last = rects[rects.length - 1];
  const ghostX = last ? last.xMax + LANE_PAD : X0;
  lanes.push(
    laneNode("lane-ghost-add", ghostX, COL_W - 2 * LANE_PAD, GHOST_LANE_H, {
      stageKey: "",
      name: "",
      count: 0,
      index: -1,
      totalStages,
      editable,
      isGhost: true,
    })
  );
  return lanes;
}
